import { useEffect, useRef, useState } from 'react';

export function SignaturePad({ title, initialName, saved, onSave }: {
  title: string;
  initialName: string;
  saved: boolean;
  onSave: (name: string, dataUrl: string) => Promise<void>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [name, setName] = useState(initialName);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(saved ? 'Signature enregistrée' : '');

  useEffect(() => { setName(initialName); }, [initialName]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(rect.width * ratio));
      canvas.height = Math.max(1, Math.round(rect.height * ratio));
      const context = canvas.getContext('2d');
      if (!context) return;
      context.scale(ratio, ratio);
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, rect.width, rect.height);
      context.strokeStyle = '#17201f';
      context.lineWidth = 2.25;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      hasInk.current = false;
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  function point(event: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function start(event: React.PointerEvent<HTMLCanvasElement>): void {
    const context = event.currentTarget.getContext('2d');
    if (!context) return;
    drawing.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    const value = point(event);
    context.beginPath();
    context.moveTo(value.x, value.y);
  }

  function move(event: React.PointerEvent<HTMLCanvasElement>): void {
    if (!drawing.current) return;
    const context = event.currentTarget.getContext('2d');
    if (!context) return;
    const value = point(event);
    context.lineTo(value.x, value.y);
    context.stroke();
    hasInk.current = true;
  }

  function stop(): void { drawing.current = false; }

  function clear(): void {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.restore();
    hasInk.current = false;
    setMessage('');
  }

  async function save(): Promise<void> {
    if (name.trim().length < 2) { setMessage('Indiquez le nom du signataire.'); return; }
    if (!hasInk.current) { setMessage('Tracez la signature dans le cadre.'); return; }
    setBusy(true);
    setMessage('');
    try {
      await onSave(name.trim(), canvasRef.current!.toDataURL('image/png'));
      setMessage('Signature enregistrée');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={`signature-card ${saved ? 'saved' : ''}`}>
      <div className="signature-heading"><strong>{title}</strong>{saved && <span>Enregistrée</span>}</div>
      <label>Nom du signataire<input value={name} onChange={(event) => setName(event.target.value)} /></label>
      <canvas
        ref={canvasRef}
        className="signature-canvas"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={stop}
        onPointerCancel={stop}
        aria-label={`Zone de signature — ${title}`}
      />
      <div className="signature-actions">
        <button className="text-button" type="button" onClick={clear}>Effacer</button>
        <button className="button compact" type="button" disabled={busy} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
      </div>
      {message && <p className="signature-message" aria-live="polite">{message}</p>}
    </section>
  );
}

